import { Radio } from 'antd'
import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useLocation, useNavigate } from 'react-router-dom'

import { ServerPrivateRegionNodeDetails } from '@/api/type-server'
import LeftArrow from '@/assets/icons/left-arrow.svg?react'
import { Divider, Trans } from '@/components'
import { Dropdown } from '@/components/base/Dropdown'
import { HostChartType } from '@/models/chart'
import { cn } from '@/utils/style'
import { TIME_UNITS_IN_SECONDS } from '@/utils/time'

import { BandwidthChart } from '../components/BandwidthChart'
import { DiskIOChart } from '../components/DiskIOChart'
import { HostChart } from '../components/HostChart'
import { MultiLineChart } from '../components/MultiLineChart'
import {
  HostMonitorItemData,
  PHYSICAL_DISK_PATTERNS,
  PHYSICAL_NIC_PATTERNS,
  useNodeMonitorData,
} from '../hooks/useNodeMonitorData'

interface NodeMonitorLocationState {
  node: ServerPrivateRegionNodeDetails
}

const GPU_CHART_TYPES = [HostChartType.NvidiaGpuUtilization, HostChartType.NvidiaGpuRamUtilization]
const BANDWIDTH_CHART_TYPES = [
  HostChartType.NetworkReceiveBytesPerSec,
  HostChartType.NetworkTransmitBytesPerSec,
]
const DISK_IO_CHART_TYPES = [
  HostChartType.DiskIOReadBytesPerSec,
  HostChartType.DiskIOWriteBytesPerSec,
]

export function NodeMonitorView() {
  const { t } = useTranslation('privateRegion')
  const location = useLocation()
  const navigate = useNavigate()
  const node = (location.state as NodeMonitorLocationState | null)?.node
  const [filterDuration, setFilterDuration] = useState(TIME_UNITS_IN_SECONDS.HOUR)

  const getStatusColor = (status: number) => {
    switch (status) {
      case 1:
        return 'text-green-500'
      case 2:
        return 'text-neutral-600'
      default:
        return 'text-red-500'
    }
  }

  const getStatusText = (status: number) => {
    switch (status) {
      case 1:
        return t('common.online')
      case 2:
        return t('common.standby')
      default:
        return t('common.offline')
    }
  }

  const {
    monitorDatas,
    networkInterfaces,
    diskInterfaces,
    gpuUtilizationDevices,
    gpuRamUtilizationDevices,
    gpuCount,
    gpuDriverVersion,
    initialLoading,
  } = useNodeMonitorData({
    nodeId: node?.resID || '',
    filterDuration,
  })

  const [selectedInterfaces, setSelectedInterfaces] = useState<string[]>([])
  const [selectedDiskDevices, setSelectedDiskDevices] = useState<string[]>([])
  const [selectedGpuUtilDevices, setSelectedGpuUtilDevices] = useState<string[]>([])
  const [selectedGpuRamDevices, setSelectedGpuRamDevices] = useState<string[]>([])

  // Initialize default selection with physical NIC
  useEffect(() => {
    if (networkInterfaces.length > 0 && selectedInterfaces.length === 0) {
      const physicalNic = networkInterfaces.find((i) =>
        PHYSICAL_NIC_PATTERNS.some((p) => p.test(i.name)),
      )
      setSelectedInterfaces(
        physicalNic ? [physicalNic.name] : [networkInterfaces[0]?.name].filter(Boolean),
      )
    }
  }, [networkInterfaces, selectedInterfaces.length])

  // Initialize default selection with physical disk
  useEffect(() => {
    if (diskInterfaces.length > 0 && selectedDiskDevices.length === 0) {
      const physicalDisk = diskInterfaces.find((i) =>
        PHYSICAL_DISK_PATTERNS.some((p) => p.test(i.name)),
      )
      setSelectedDiskDevices(
        physicalDisk ? [physicalDisk.name] : [diskInterfaces[0]?.name].filter(Boolean),
      )
    }
  }, [diskInterfaces, selectedDiskDevices.length])

  // Initialize default selection for GPU Utilization (first GPU)
  useEffect(() => {
    if (gpuUtilizationDevices.length > 0 && selectedGpuUtilDevices.length === 0) {
      setSelectedGpuUtilDevices([gpuUtilizationDevices[0].name])
    }
  }, [gpuUtilizationDevices, selectedGpuUtilDevices.length])

  // Initialize default selection for GPU RAM Utilization (first GPU)
  useEffect(() => {
    if (gpuRamUtilizationDevices.length > 0 && selectedGpuRamDevices.length === 0) {
      setSelectedGpuRamDevices([gpuRamUtilizationDevices[0].name])
    }
  }, [gpuRamUtilizationDevices, selectedGpuRamDevices.length])

  const hasGpuData = gpuUtilizationDevices.length > 0 || gpuRamUtilizationDevices.length > 0
  const systemCharts = monitorDatas.filter(
    (item) =>
      !GPU_CHART_TYPES.includes(item.type) &&
      !BANDWIDTH_CHART_TYPES.includes(item.type) &&
      !DISK_IO_CHART_TYPES.includes(item.type),
  )

  const handleBack = () => {
    navigate('/private-region/compute')
  }

  return (
    <div className="card bg-panel-white">
      <div className="flex items-center justify-between pb-8">
        <div className="flex items-center gap-4">
          <div onClick={handleBack} className="w-11 h-11 icon-btn cursor-pointer">
            <LeftArrow />
          </div>
          <div>
            <div className="h4">{node?.gpuModel || node?.cpuModel || 'Node Monitor'}</div>
            <div className="t-sm mt-0.5 flex gap-2">
              {node?.regionInfo?.name && (
                <>
                  <span className="text-secondary">{node.regionInfo.name}</span>
                  <span className="text-secondary">·</span>
                  <span className={cn(getStatusColor(node.status))}>
                    {getStatusText(node.status)}
                  </span>
                </>
              )}
            </div>
          </div>
        </div>
        <Radio.Group
          onChange={(e) => setFilterDuration(e.target.value)}
          value={filterDuration}
          className="t-x-sb"
        >
          <Radio.Button value={TIME_UNITS_IN_SECONDS.HOUR}>
            {t('nodeMonitor.realtime')}
          </Radio.Button>
          <Radio.Button value={TIME_UNITS_IN_SECONDS.DAY}>{t('nodeMonitor.day1')}</Radio.Button>
          <Radio.Button value={3 * TIME_UNITS_IN_SECONDS.DAY}>
            {t('nodeMonitor.days3')}
          </Radio.Button>
        </Radio.Group>
      </div>

      <Divider />

      {initialLoading && monitorDatas.length === 0 ? (
        <div className="h-16 overflow-hidden mt-4 text-center text-tertiary t-sm">
          {t('nodeMonitor.loading')}
        </div>
      ) : (
        <>
          {/* GPU Acceleration Cluster Section */}
          <div className="flex items-center gap-2 py-2 mb-4">
            <div className="w-1 h-5 bg-btn-secondary"></div>
            <div className="h6">
              <Trans ns="privateRegion" i18nKey="nodeMonitor.gpuAccelerationCluster">
                GPU Acceleration Cluster
              </Trans>
            </div>
          </div>

          {hasGpuData ? (
            <>
              <div className="grid grid-cols-2 gap-8 mb-4">
                <div className="p-6 border rounded-2xl">
                  <div className="t-bold mb-2">
                    <Trans ns="privateRegion" i18nKey="nodeMonitor.gpuInformation">
                      GPU Information
                    </Trans>
                  </div>
                  <div className="t-sm text-tertiary">
                    {node?.gpuModel} {node?.gpuModel ? `* ${gpuCount}` : '-'}
                  </div>
                </div>
                <div className="p-6 border rounded-2xl">
                  <div className="t-bold mb-2">
                    <Trans ns="privateRegion" i18nKey="nodeMonitor.driver">
                      Driver
                    </Trans>
                  </div>
                  <div className="t-sm text-tertiary">{gpuDriverVersion || '-'}</div>
                </div>
              </div>
              <div className="text-tertiary gap-8 grid grid-cols-2 mb-8 px-6 pt-6 pb-10 border rounded-2xl">
                {/* GPU Utilization */}
                <div>
                  <div className="mb-2">
                    <div className="flex items-center justify-between gap-2">
                      <div className="text-primary t-sb">
                        {t('nodeMonitor.chartTitle.gpuUtilization')}
                      </div>
                      {gpuUtilizationDevices.length > 1 && (
                        <Dropdown
                          multiple
                          rootClassName="w-32"
                          className="h-11"
                          menuClassName="max-h-[200px]"
                          options={gpuUtilizationDevices.map((d) => ({
                            label: (
                              <span className="text-primary truncate block max-w-[80px]">
                                GPU {String(Number(d.name) + 1).padStart(2, '0')}
                              </span>
                            ),
                            value: d.name,
                          }))}
                          value={selectedGpuUtilDevices}
                          onChange={(v) => {
                            const newSelection = v as string[]
                            if (newSelection.length > 0) {
                              setSelectedGpuUtilDevices([newSelection[newSelection.length - 1]])
                            }
                          }}
                          placeholder="Select GPU"
                        />
                      )}
                    </div>
                  </div>
                  <MultiLineChart
                    key={`gpu-util-${filterDuration}-${selectedGpuUtilDevices.join(',')}`}
                    data={gpuUtilizationDevices}
                    selectedItems={selectedGpuUtilDevices}
                    filterDuration={filterDuration}
                  />
                </div>

                {/* GPU RAM Utilization */}
                <div>
                  <div className="mb-2">
                    <div className="flex items-center justify-between gap-2">
                      <div className="text-primary t-sb">
                        {t('nodeMonitor.chartTitle.gpuRamUtilization')}
                      </div>
                      {gpuRamUtilizationDevices.length > 1 && (
                        <Dropdown
                          multiple
                          rootClassName="w-32"
                          className="h-11"
                          menuClassName="max-h-[200px]"
                          options={gpuRamUtilizationDevices.map((d) => ({
                            label: (
                              <span className="text-primary truncate block max-w-[80px]">
                                GPU {String(Number(d.name) + 1).padStart(2, '0')}
                              </span>
                            ),
                            value: d.name,
                          }))}
                          value={selectedGpuRamDevices}
                          onChange={(v) => {
                            const newSelection = v as string[]
                            if (newSelection.length > 0) {
                              setSelectedGpuRamDevices([newSelection[newSelection.length - 1]])
                            }
                          }}
                          placeholder="Select GPU"
                        />
                      )}
                    </div>
                  </div>
                  <MultiLineChart
                    key={`gpu-ram-${filterDuration}-${selectedGpuRamDevices.join(',')}`}
                    data={gpuRamUtilizationDevices}
                    selectedItems={selectedGpuRamDevices}
                    filterDuration={filterDuration}
                  />
                </div>
              </div>
            </>
          ) : (
            <div className="h-16 overflow-hidden mt-4 text-center text-tertiary t-sm mb-8">
              <Trans ns="privateRegion" i18nKey="common.noData">
                No Data
              </Trans>
            </div>
          )}

          {/* System Metrics Section */}
          <div className="flex items-center gap-2 py-2 mb-4">
            <div className="w-1 h-5 bg-btn-secondary"></div>
            <div className="h6">
              <Trans ns="privateRegion" i18nKey="nodeMonitor.systemMetrics">
                System Metrics
              </Trans>
            </div>
          </div>
          <div className="text-tertiary gap-8 grid grid-cols-2 px-6 pt-6 pb-10 border rounded-2xl">
            {systemCharts.map((item: HostMonitorItemData) => (
              <div key={item.type}>
                <div className="mb-2 flex justify-between items-center">
                  <div className="flex t-sb">
                    <div className="text-primary">{t(item.titleKey)}</div>
                  </div>
                  <div className="bg-panel-gray-100 flex items-center py-2 px-3 t-bold gap-3">
                    <div className="flex items-center">
                      <div className="t-sb text-primary">{item.realtimeValue}</div>
                    </div>
                  </div>
                </div>
                <HostChart key={`${item.type}-${filterDuration}`} value={item} t={t} />
              </div>
            ))}

            {/* Bandwidth Chart */}
            <div>
              <div className="mb-2">
                <div className="flex flex-col t-sb">
                  <div className="flex items-center justify-between gap-2">
                    <div className="text-primary">{t('nodeMonitor.chartTitle.bandwidthUsage')}</div>
                    {networkInterfaces.length > 0 && (
                      <Dropdown
                        multiple
                        rootClassName="w-40"
                        className="h-11"
                        menuClassName="max-h-[200px]"
                        options={networkInterfaces.map((i) => ({
                          label: (
                            <span
                              className="text-primary truncate block max-w-[120px]"
                              title={i.name}
                            >
                              {i.name}
                            </span>
                          ),
                          value: i.name,
                        }))}
                        value={selectedInterfaces}
                        onChange={(v) => {
                          const newSelection = v as string[]
                          if (newSelection.length > 0) {
                            setSelectedInterfaces([newSelection[newSelection.length - 1]])
                          }
                        }}
                        placeholder="Select interfaces"
                      />
                    )}
                  </div>
                  <div className="flex gap-4 mt-1">
                    <div className="flex items-center gap-1">
                      <div className="w-3 h-0.5 bg-highlight"></div>
                      <span className="t-xs text-secondary">TX</span>
                    </div>
                    <div className="flex items-center gap-1">
                      <div className="w-3 h-0.5 bg-warning"></div>
                      <span className="t-xs text-secondary">RX</span>
                    </div>
                  </div>
                </div>
              </div>
              <BandwidthChart
                key={`bandwidth-${filterDuration}-${selectedInterfaces.join(',')}`}
                interfaces={networkInterfaces}
                selectedInterfaces={selectedInterfaces}
                filterDuration={filterDuration}
              />
            </div>

            {/* Disk I/O Chart */}
            <div>
              <div className="mb-2">
                <div className="flex flex-col t-sb">
                  <div className="flex items-center justify-between gap-2">
                    <div className="text-primary">{t('nodeMonitor.chartTitle.diskIOUsage')}</div>
                    {diskInterfaces.length > 0 && (
                      <Dropdown
                        multiple
                        rootClassName="w-40"
                        className="h-11"
                        menuClassName="max-h-[200px]"
                        options={diskInterfaces.map((i) => ({
                          label: (
                            <span
                              className="text-primary truncate block max-w-[120px]"
                              title={i.name}
                            >
                              {i.name}
                            </span>
                          ),
                          value: i.name,
                        }))}
                        value={selectedDiskDevices}
                        onChange={(v) => {
                          const newSelection = v as string[]
                          if (newSelection.length > 0) {
                            setSelectedDiskDevices([newSelection[newSelection.length - 1]])
                          }
                        }}
                        placeholder="Select device"
                      />
                    )}
                  </div>
                  <div className="flex gap-4 mt-1">
                    <div className="flex items-center gap-1">
                      <div className="w-3 h-0.5 bg-highlight"></div>
                      <span className="t-xs text-secondary">Read</span>
                    </div>
                    <div className="flex items-center gap-1">
                      <div className="w-3 h-0.5 bg-warning"></div>
                      <span className="t-xs text-secondary">Write</span>
                    </div>
                  </div>
                </div>
              </div>
              <DiskIOChart
                key={`diskio-${filterDuration}-${selectedDiskDevices.join(',')}`}
                interfaces={diskInterfaces}
                selectedDevices={selectedDiskDevices}
                filterDuration={filterDuration}
              />
            </div>
          </div>
        </>
      )}
    </div>
  )
}
